import { twMerge } from 'tailwind-merge';

import useHiddenComponents from '@/data/hook/useHiddenComponent';
import User from '@/models/User';
import HideContainer, {
  HideContainerProps,
} from '@/components/ui/custom/HideContainer';
import { isTeacher } from '@/functions/auth';
import TeachingWeekHeader from './TeachingWeekHeader';
import TeachingWeekdays from './TeachingWeekdays';
import { ListKlassesFilters } from '@/data/services/klassServices';
import { KlassStatusEnum } from '@/models/Klass';
import { useDynamicParams } from '@/data/hook/useDynamicParams';
import { searchParamsToObject } from '@/utils/searchParamsToObject';
import {
  klassConfigFields,
  OmitParamsListKlassesFilters,
} from '@/data/hook/useKlassFilters';
import { buildChangedObject } from '@/utils/buildChangedObject';

export type TeachingWeekProps = Pick<
  HideContainerProps,
  'updateRender' | 'render' | 'className' | 'setRef'
> & {
  user: User;
};

export default function TeachingWeek(props: TeachingWeekProps) {
  const { user, className } = props;

  const { components } = useHiddenComponents();

  const component = components['teachingWeek'];

  const { params, updateParams } =
    useDynamicParams<OmitParamsListKlassesFilters>({
      klassStatus: [KlassStatusEnum.IN_PROGRESS, KlassStatusEnum.OPEN],
      ...(isTeacher(user.userType) && { teacherId: [user.id.toString()] }),
    });

  const filter = searchParamsToObject<OmitParamsListKlassesFilters>(
    params,
    klassConfigFields,
  );

  function onChange(data: OmitParamsListKlassesFilters) {
    const changedFields = buildChangedObject(filter, data);

    const updatedFilters: Partial<OmitParamsListKlassesFilters> = { ...data };

    if ('klassStartDateOrdering' in changedFields) {
      updatedFilters.klassEndDateOrdering = undefined;
    }

    if ('klassEndDateOrdering' in changedFields) {
      updatedFilters.klassStartDateOrdering = undefined;
    }

    updateParams({ ...updatedFilters, pageNumber: 1 });
  }

  function filterAdapter(
    filter: OmitParamsListKlassesFilters,
  ): ListKlassesFilters {
    const omited = {
      ...filter,
      klassStartDateOrdering: undefined,
      klassEndDateOrdering: undefined,
      klassId: undefined,
      klassStatus: undefined,
    };

    return {
      ...omited,
      status: filter.klassStatus,
      ordering:
        filter.klassStartDateOrdering ??
        filter.klassEndDateOrdering ??
        'day_time',
      id: filter.klassId,
    };
  }

  const filterAdapted = filterAdapter(filter);

  return (
    <HideContainer
      {...props}
      icon={component.icon}
      title={component.title}
      type={component.id}
      header={
        <TeachingWeekHeader user={user} update={onChange} filter={filter} />
      }
      className={{
        header: twMerge(
          'gap-5 p-0 items-start flex-col lg:flex-row',
          className?.header,
        ),
        section: twMerge('flex flex-col gap-3.5 p-4', className?.section),
      }}
    >
      <TeachingWeekdays filters={filterAdapted} />
    </HideContainer>
  );
}
